import { Assets, Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import type {
  IInteractableVisual,
  InteractableVisualDiagnostics,
  InteractableVisualOptions,
} from './IRenderer';
import { PixiOverlayHost } from './PixiOverlayHost';
import { Tween, Easing } from './Tween';
import { Z_LAYERS } from './Z_LAYERS';
import { getItemIconSVG } from './items/ItemIconRegistry';
import { LOOT } from '../../shared/config/loot.config';
import { prefersReducedMotion } from '../systems/accessibility/motionPrefs';
import { livePlayerPosition } from '../systems/player/livePlayerPosition';
import { PLAYER_HITBOX } from '../../shared/logic/CombatAnchors';

const RARITY_GLOW: Record<string, number> = {
  common: 0xb6bdc9,
  uncommon: 0x45d16f,
  rare: 0x4b9cff,
  epic: 0xc56cff,
  unique: 0xffbd45,
};

/** Common pickups glow warm lantern-white, not the grey rarity rim. */
const WORLD_GLOW_COMMON = 0xffe9b0;
/** How often (ms) a pickup checks whether the player is near its label. */
const LABEL_CHECK_MS = 120;

/**
 * PixiInteractableVisual — IInteractableVisual impl using a PIXI.Container
 * on the overlay's worldContainer. Bob animation runs via the substrate-
 * agnostic Tween primitive (Stage 4b) driven by PixiOverlayHost's
 * tick listener.
 *
 * Lazy-builds the underlying Pixi nodes after PixiOverlayHost.ensureReady()
 * resolves. Calls to destroy() before that promise resolves are honored
 * via the `destroyed` flag — prevents the parts from being attached after
 * the consumer has already given up on the visual.
 */
export class PixiInteractableVisual implements IInteractableVisual {
  private container: Container | null = null;
  private destroyed = false;
  private unsubscribeTick: (() => void) | null = null;
  private landing: Tween | null = null;
  private bob: Tween | null = null;
  private shimmer: Tween | null = null;
  private departure: Tween | null = null;
  private scaleTween: Tween | null = null;
  private scaleState = { value: 1 };
  private glow: Graphics | null = null;
  private label: Text | null = null;
  private labelTarget = 0;
  private labelCheckMs = LABEL_CHECK_MS;
  private departureComplete: (() => void) | null = null;
  private baseY = 0;
  private motionReduced = false;
  private motionPhase: InteractableVisualDiagnostics['phase'] = 'loading';
  private departureReason: InteractableVisualDiagnostics['departureReason'] = null;

  constructor(opts: InteractableVisualOptions) {
    void PixiOverlayHost.ensureReady().then(() => {
      if (this.destroyed) return;
      this.build(opts);
    });
  }

  private build(opts: InteractableVisualOptions): void {
    const { x, y, itemId, displayName, icon = '◆', rarity, quantity } = opts;
    const rarityColor = RARITY_GLOW[rarity] ?? 0xffffff;

    // Its own render group (lane AL, plan 21 §10): the bob, landing and despawn
    // tweens move this container every frame. In the world's group that re-packed
    // every vertex of the frame, glow and shadow each frame (~50k vertices/s in a
    // First Field fight), and the async thumbnail swap rebuilt the whole world's
    // instruction set. As a group, a move is one transform and the swap rebuilds
    // only this drop.
    const container = new Container({ isRenderGroup: true });
    container.x = x;
    container.y = y;
    container.zIndex = Z_LAYERS.INTERACTABLES;

    // World look (stream notes 16 + DJ's Tidewend fish, 2026-10-08): a
    // pickup is the item itself lying in the world — a contact shadow, a soft
    // pool of light under it and a halo behind it that breathes — never a UI
    // tile. Every part is drawn once; motion is transform/alpha only.
    const glowColor = rarity === 'common' ? WORLD_GLOW_COMMON : rarityColor;
    const shadow = new Graphics();
    shadow.ellipse(0, 17, 14, 4).fill({ color: 0x000000, alpha: 0.38 });
    shadow.ellipse(0, 16, 21, 6).fill({ color: glowColor, alpha: 0.16 });
    shadow.ellipse(0, 16, 12, 3.5).fill({ color: glowColor, alpha: 0.22 });
    container.addChild(shadow);

    const glow = new Graphics();
    // Soft and small: a glimmer behind the item, not a disc it sits on.
    glow.circle(0, 2, 15).fill({ color: glowColor, alpha: 0.28 });
    glow.circle(0, 2, 9).fill({ color: glowColor, alpha: 0.4 });
    glow.alpha = LOOT.presentation.shimmerAlphaMin;
    container.addChild(glow);
    this.glow = glow;

    // Glyph placeholder is replaced asynchronously by the exact same item
    // thumbnail used in inventory. It is always a real glyph—never an
    // internal `material_*` texture key.
    const iconText = new Text({
      text: icon,
      style: {
        fontFamily: 'sans-serif',
        fontSize: 22,
        fill: 0xffffff,
        stroke: { color: 0x000000, width: 1.5 },
      },
    });
    iconText.anchor.set(0.5);
    container.addChild(iconText);

    if (itemId) {
      void this.loadItemThumbnail(container, iconText, itemId);
    }

    if (displayName) {
      // The name shows only while the player is near (faded by alpha).
      const nameText = new Text({
        text: displayName,
        style: {
          fontFamily: 'Inter, sans-serif',
          fontSize: 10,
          fontWeight: '600',
          fill: 0xfff6dc,
          stroke: { color: 0x05070c, width: 2.5 },
        },
      });
      nameText.anchor.set(0.5, 0);
      nameText.y = 24;
      nameText.alpha = 0;
      container.addChild(nameText);
      this.label = nameText;
    }

    if (quantity !== undefined && quantity > 1) {
      const qtyBadge = new Graphics();
      qtyBadge.circle(14, -12, 7)
        .fill({ color: 0x080b12, alpha: 0.8 })
        .stroke({ color: glowColor, alpha: 0.8, width: 1 });
      container.addChild(qtyBadge);

      const qtyText = new Text({
        text: quantity.toString(),
        style: {
          fontFamily: 'monospace',
          fontSize: 9,
          fontWeight: '700',
          fill: 0xffffff,
          stroke: { color: 0x000000, width: 1.5 },
        },
      });
      qtyText.anchor.set(0.5);
      qtyText.x = 14;
      qtyText.y = -12;
      container.addChild(qtyText);
    }

    PixiOverlayHost.addWorldChild(container);
    this.container = container;
    this.baseY = y;
    this.motionReduced = prefersReducedMotion();

    this.unsubscribeTick = PixiOverlayHost.addTickListener((deltaMs) => {
      const live = this.container;
      if (!live) return;
      if (!this.departure) this.syncMotionPreference();
      if (this.bob && !this.bob.step(deltaMs)) this.bob = null;
      if (this.shimmer && !this.shimmer.step(deltaMs)) this.shimmer = null;
      if (this.landing && !this.landing.step(deltaMs)) this.landing = null;
      if (this.scaleTween && !this.scaleTween.step(deltaMs)) this.scaleTween = null;
      if (this.container) this.container.scale.set(this.scaleState.value);
      if (this.departure && !this.departure.step(deltaMs)) this.departure = null;
      this.stepLabel(deltaMs);
    });

    this.startLandPhase(y);
  }

  /** Fade the name in while the player stands near, out when they leave. */
  private stepLabel(deltaMs: number): void {
    const label = this.label;
    const container = this.container;
    if (!label || !container) return;
    this.labelCheckMs += deltaMs;
    if (this.labelCheckMs >= LABEL_CHECK_MS) {
      this.labelCheckMs = 0;
      const at = livePlayerPosition();
      const dx = at.x + PLAYER_HITBOX.width / 2 - container.x;
      const dy = at.y + PLAYER_HITBOX.height / 2 - this.baseY;
      const near = this.departure === null && dx * dx + dy * dy <= LOOT.presentation.labelNearPx ** 2;
      this.labelTarget = near ? 1 : 0;
    }
    if (label.alpha === this.labelTarget) return;
    const step = deltaMs / LOOT.presentation.labelFadeMs;
    label.alpha = this.labelTarget > label.alpha
      ? Math.min(this.labelTarget, label.alpha + step)
      : Math.max(this.labelTarget, label.alpha - step);
  }

  private startLandPhase(baseY: number): void {
    const container = this.container;
    if (!container) return;
    if (this.motionReduced) {
      this.motionPhase = 'idle';
      container.y = baseY;
      container.alpha = 1;
      container.scale.set(1);
      return;
    }

    this.motionPhase = 'landing';
    container.y = baseY - LOOT.presentation.landLiftPx;
    container.alpha = 0;
    this.scaleState.value = LOOT.presentation.landStartScale;
    container.scale.set(this.scaleState.value);
    this.landing = new Tween(container, {
      to: { y: baseY, alpha: 1 },
      durationMs: LOOT.presentation.landDurationMs,
      ease: Easing.cubicOut,
      onComplete: () => this.startIdlePhase(baseY),
    });
    this.scaleTween = new Tween(this.scaleState, {
      to: { value: 1 },
      durationMs: LOOT.presentation.landDurationMs,
      ease: Easing.cubicOut,
    });
  }

  private startIdlePhase(baseY: number): void {
    const container = this.container;
    if (!container || this.motionReduced) return;
    this.motionPhase = 'idle';
    this.bob = new Tween(container, {
      to: { y: baseY - LOOT.presentation.idleBobOffsetPx },
      durationMs: LOOT.presentation.idleBobLegDurationMs,
      ease: Easing.sineInOut,
      yoyo: true,
      repeat: -1,
    });
    if (this.glow) {
      this.glow.alpha = LOOT.presentation.shimmerAlphaMin;
      this.shimmer = new Tween(this.glow, {
        to: { alpha: LOOT.presentation.shimmerAlphaMax },
        durationMs: LOOT.presentation.shimmerLegDurationMs,
        ease: Easing.sineInOut,
        yoyo: true,
        repeat: -1,
      });
    }
  }

  private syncMotionPreference(): void {
    const reduced = prefersReducedMotion();
    if (reduced === this.motionReduced) return;
    this.motionReduced = reduced;
    if (!this.container) return;

    this.stopAmbientMotion();
    if (reduced) {
      this.container.y = this.baseY;
      this.container.alpha = 1;
      this.scaleState.value = 1;
      this.container.scale.set(1);
      if (this.glow) this.glow.alpha = LOOT.presentation.shimmerAlphaMin;
    } else {
      // A live preference change should not replay the arrival arc; it merely
      // enables the calm idle loop from the authored resting anchor.
      this.startIdlePhase(this.baseY);
    }
  }

  playCollect(target: { x: number; y: number }, onComplete: () => void): void {
    const container = this.container;
    if (this.destroyed || !container) {
      onComplete();
      return;
    }

    this.stopMotion();
    this.motionPhase = 'collecting';
    this.departureReason = 'collect';
    this.departureComplete = onComplete;
    const reduced = prefersReducedMotion();
    const durationMs = reduced
      ? LOOT.presentation.reducedMotionFadeMs
      : LOOT.presentation.collectDurationMs;
    const destination = reduced
      ? { x: container.x, y: container.y }
      : { x: target.x, y: target.y - LOOT.presentation.collectTargetOffsetYPx };
    this.departure = new Tween(container, {
      to: { ...destination, alpha: 0 },
      durationMs,
      ease: reduced ? Easing.linear : Easing.power2,
      onComplete: () => this.completeDeparture(),
    });
    if (!reduced) {
      this.scaleState.value = container.scale.x;
      this.scaleTween = new Tween(this.scaleState, {
        to: { value: LOOT.presentation.collectEndScale },
        durationMs,
        ease: Easing.power2,
      });
    }
  }

  playNeutralRemoval(
    reason: 'picked_up' | 'despawn' | 'reconciliation',
    onComplete: () => void,
  ): void {
    const container = this.container;
    if (this.destroyed || !container) {
      onComplete();
      return;
    }

    this.stopMotion();
    this.motionPhase = 'removing';
    this.departureReason = reason;
    this.departureComplete = onComplete;
    const reduced = prefersReducedMotion();
    const durationMs = reduced
      ? LOOT.presentation.reducedMotionFadeMs
      : reason === 'despawn'
        ? LOOT.presentation.despawnRemovalDurationMs
        : reason === 'reconciliation'
          ? LOOT.presentation.reconciliationRemovalDurationMs
          : LOOT.presentation.neutralRemovalDurationMs;
    const destination: Record<string, number> = reason === 'despawn' && !reduced
      ? { y: container.y + LOOT.presentation.despawnSinkYPx, alpha: 0 }
      : { alpha: 0 };
    this.departure = new Tween(container, {
      to: destination,
      durationMs,
      ease: Easing.power2Out,
      onComplete: () => this.completeDeparture(),
    });
  }

  private stopMotion(): void {
    this.stopAmbientMotion();
    this.departure?.kill();
    this.departure = null;
  }

  private stopAmbientMotion(): void {
    this.landing?.kill();
    this.landing = null;
    this.bob?.kill();
    this.bob = null;
    this.shimmer?.kill();
    this.shimmer = null;
    this.scaleTween?.kill();
    this.scaleTween = null;
  }

  private completeDeparture(): void {
    const onComplete = this.departureComplete;
    this.departureComplete = null;
    onComplete?.();
  }

  private async loadItemThumbnail(container: Container, placeholder: Text, itemId: string): Promise<void> {
    try {
      const texture = await Assets.load<Texture>({
        src: getItemIconSVG(itemId, 40),
        alias: `ground-drop:${itemId}`,
      });
      if (this.destroyed || this.container !== container) return;

      const sprite = new Sprite(texture);
      sprite.anchor.set(0.5);
      sprite.width = 30;
      sprite.height = 30;
      const placeholderIndex = container.getChildIndex(placeholder);
      container.addChildAt(sprite, Math.max(0, placeholderIndex));
      placeholder.destroy();
    } catch {
      // Keep the clean glyph fallback if SVG decoding/loading fails.
    }
  }

  getDiagnostics(): InteractableVisualDiagnostics {
    return {
      phase: this.motionPhase,
      departureReason: this.departureReason,
      x: this.container?.x ?? null,
      y: this.container?.y ?? null,
      alpha: this.container?.alpha ?? null,
      scale: this.container?.scale.x ?? null,
      reducedMotion: this.motionReduced,
    };
  }

  destroy(): void {
    this.destroyed = true;
    this.motionPhase = 'destroyed';
    this.stopMotion();
    this.departureComplete = null;
    this.glow = null;
    this.label = null;
    this.unsubscribeTick?.();
    this.unsubscribeTick = null;
    if (this.container) {
      this.container.destroy({ children: true });
      this.container = null;
    }
  }
}
